import React, { useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import axios from "axios";
import {
    Alert,
    alpha,
    Box,
    Button,
    Card,
    CardActionArea,
    CardContent,
    Chip,
    CircularProgress,
    Container,
    Grid,
    Paper,
    Stack,
    Typography,
} from "@mui/material";
import ClassOutlinedIcon from "@mui/icons-material/ClassOutlined";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import HourglassTopOutlinedIcon from "@mui/icons-material/HourglassTopOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";
import { useFormTeacherHomeroom } from "../../hooks/useFormTeacherHomeroom";
import { useHomeroomTeacherTerm } from "../../hooks/useHomeroomTeacherTerm";
import { getApiBaseUrl } from "../../config/api";
import { getSchoolIdForUser } from "../../utils/userContext";
import { useSelector } from "react-redux";

function TaskCard({ to, title, subtitle, icon: Icon, color = "primary" }) {
    return (
        <Grid item xs={12} sm={6}>
            <Card
                elevation={0}
                sx={{
                    height: "100%",
                    borderRadius: 2.5,
                    border: (t) => `1px solid ${alpha(t.palette.divider, 0.9)}`,
                }}
            >
                <CardActionArea
                    component={RouterLink}
                    to={to}
                    sx={{
                        height: "100%",
                        p: 0.5,
                        "&:hover": { bgcolor: (t) => alpha(t.palette[color].main, 0.04) },
                    }}
                >
                    <CardContent>
                        <Stack direction="row" spacing={2} alignItems="flex-start">
                            <Box
                                sx={{
                                    width: 48,
                                    height: 48,
                                    borderRadius: 2,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    bgcolor: (t) => alpha(t.palette[color].main, 0.12),
                                    color: `${color}.main`,
                                }}
                            >
                                <Icon />
                            </Box>
                            <Box>
                                <Typography variant="subtitle1" fontWeight={800}>
                                    {title}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {subtitle}
                                </Typography>
                            </Box>
                        </Stack>
                    </CardContent>
                </CardActionArea>
            </Card>
        </Grid>
    );
}

const TeacherMyClass = () => {
    const { currentUser } = useSelector((s) => s.user);
    const schoolId = getSchoolIdForUser(currentUser);
    const homeroomTerm = useHomeroomTeacherTerm();
    const {
        homeroomClasses,
        homeroomClassId,
        homeroomLabel,
        roleIsHomeroom,
        needsHomeroomSetup,
        loading,
        refresh,
    } = useFormTeacherHomeroom();

    const [stats, setStats] = useState({ verified: 0, pending: 0, rejected: 0 });
    const [statsLoading, setStatsLoading] = useState(false);

    useEffect(() => {
        if (!schoolId || !homeroomClassId || needsHomeroomSetup) {
            setStats({ verified: 0, pending: 0, rejected: 0 });
            return;
        }
        let cancelled = false;
        setStatsLoading(true);
        (async () => {
            try {
                const { data } = await axios.get(`${getApiBaseUrl()}/Enrollment/school/${schoolId}`);
                if (cancelled) return;
                const list = Array.isArray(data) ? data : [];
                const inClass = list.filter((s) => {
                    const sid = s.sclass?._id || s.sclass?.id || s.sclassName;
                    return String(sid) === String(homeroomClassId);
                });
                setStats({
                    verified: inClass.filter((s) => s.enrollmentStatus === "Verified").length,
                    pending: inClass.filter((s) => s.enrollmentStatus === "Pending").length,
                    rejected: inClass.filter((s) => s.enrollmentStatus === "Rejected").length,
                });
            } catch {
                if (!cancelled) setStats({ verified: 0, pending: 0, rejected: 0 });
            } finally {
                if (!cancelled) setStatsLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [schoolId, homeroomClassId, needsHomeroomSetup]);

    const classCount = useMemo(() => {
        const c = homeroomClasses[0];
        return c?._count?.students ?? null;
    }, [homeroomClasses]);

    if (needsHomeroomSetup && loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (needsHomeroomSetup) {
        return (
            <Container maxWidth="sm" sx={{ py: 4 }}>
                <Alert severity="warning" sx={{ borderRadius: 2, mb: 2 }}>
                    <strong>My class</strong> is not linked yet. Your director must:
                    <Box component="ol" sx={{ mt: 1, mb: 0, pl: 2.5 }}>
                        <li>
                            Open <strong>School office → Staff</strong>, find your name, click green{" "}
                            <strong>Assign class</strong> (pick e.g. Form 1 west).
                        </li>
                        <li>
                            Set your role to <strong>{homeroomTerm.roleTitleCaps}</strong> if not already done.
                        </li>
                    </Box>
                    {roleIsHomeroom ? (
                        <> Role is set — only the class link is missing.</>
                    ) : (
                        <> You may still be listed as a subject teacher only.</>
                    )}
                </Alert>
                <Stack direction="row" spacing={1}>
                    <Button variant="contained" onClick={() => refresh()}>
                        Refresh
                    </Button>
                    <Button component={RouterLink} to="/Teacher/dashboard" variant="outlined">
                        Back to home
                    </Button>
                </Stack>
            </Container>
        );
    }

    return (
        <Box
            sx={{
                minHeight: "100%",
                py: { xs: 2, sm: 3 },
                background: (t) =>
                    t.palette.mode === "light"
                        ? `linear-gradient(180deg, ${alpha(t.palette.primary.main, 0.05)} 0%, ${t.palette.grey[100]} 24%)`
                        : alpha(t.palette.common.black, 0.15),
            }}
        >
            <Container maxWidth="lg">
                <Paper
                    elevation={0}
                    sx={{
                        p: { xs: 2.5, sm: 3 },
                        mb: 3,
                        borderRadius: 3,
                        color: "common.white",
                        background: (t) =>
                            `linear-gradient(120deg, ${t.palette.grey[900]} 0%, ${t.palette.primary.dark} 55%, ${t.palette.primary.main} 100%)`,
                    }}
                >
                    <Stack direction="row" spacing={2} alignItems="center">
                        <ClassOutlinedIcon sx={{ fontSize: 40, opacity: 0.9 }} />
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="overline" sx={{ opacity: 0.9 }}>
                                My class
                            </Typography>
                            <Typography variant="h4" fontWeight={800} lineHeight={1.15}>
                                {homeroomLabel}
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.92, mt: 0.5 }}>
                                Everything for your class is here — enroll pupils, check director approval, attendance,
                                results, and report cards.
                            </Typography>
                        </Box>
                    </Stack>
                    <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 2 }}>
                        <Chip
                            size="small"
                            icon={<CheckCircleOutlineIcon sx={{ color: "inherit !important" }} />}
                            label={
                                statsLoading
                                    ? "Loading…"
                                    : `${stats.verified} verified on roll`
                            }
                            sx={{ bgcolor: alpha("#fff", 0.14), color: "inherit", fontWeight: 600 }}
                        />
                        <Chip
                            size="small"
                            icon={<HourglassTopOutlinedIcon sx={{ color: "inherit !important" }} />}
                            label={`${stats.pending} waiting for director`}
                            sx={{ bgcolor: alpha("#fff", 0.14), color: "inherit", fontWeight: 600 }}
                        />
                        {classCount != null ? (
                            <Chip
                                size="small"
                                label={`${classCount} active slots`}
                                sx={{ bgcolor: alpha("#fff", 0.1), color: "inherit" }}
                            />
                        ) : null}
                        {roleIsHomeroom ? (
                            <Chip
                                size="small"
                                label={homeroomTerm.roleTitleCaps}
                                sx={{ bgcolor: alpha("#fff", 0.1), color: "inherit" }}
                            />
                        ) : null}
                    </Stack>
                </Paper>

                {stats.pending > 0 ? (
                    <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                        <strong>{stats.pending}</strong> student(s) are waiting for the director to verify. They cannot
                        get fees or full access until approved.
                    </Alert>
                ) : null}

                <Typography variant="subtitle2" fontWeight={700} color="text.secondary" sx={{ mb: 1.5 }}>
                    What do you want to do?
                </Typography>
                <Grid container spacing={2}>
                    <TaskCard
                        to="/Teacher/my-class/students"
                        title="View my students"
                        subtitle="See everyone in your class and open their profiles"
                        icon={PeopleAltOutlinedIcon}
                        color="primary"
                    />
                    <TaskCard
                        to="/Teacher/enroll-student"
                        title="Enroll a new student"
                        subtitle="Add a pupil — stays pending until the director approves"
                        icon={PersonAddAlt1Icon}
                        color="warning"
                    />
                    <TaskCard
                        to="/Teacher/my-class/students"
                        title="Enrollment status"
                        subtitle={`${stats.pending} pending · ${stats.verified} approved — open roster for details`}
                        icon={HourglassTopOutlinedIcon}
                        color="info"
                    />
                    <TaskCard
                        to="/Teacher/attendance-entry"
                        title="Class attendance"
                        subtitle="Mark who is present today and send to the director"
                        icon={FactCheckOutlinedIcon}
                        color="success"
                    />
                    <TaskCard
                        to="/Teacher/form-teacher-results"
                        title="Test results & marks"
                        subtitle="Review subject teachers' marks for your class"
                        icon={ArticleOutlinedIcon}
                        color="secondary"
                    />
                    <TaskCard
                        to="/Teacher/form-teacher-results"
                        title="Report cards → director"
                        subtitle="Build report cards and submit the class package for approval"
                        icon={SendOutlinedIcon}
                        color="primary"
                    />
                    <TaskCard
                        to="/Teacher/disciplinary"
                        title="Disciplinary cases"
                        subtitle="Report offenses — director verifies warning, suspension, or expulsion"
                        icon={GavelOutlinedIcon}
                        color="error"
                    />
                </Grid>
            </Container>
        </Box>
    );
};

export default TeacherMyClass;
